import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchProjectAttendance,
} from "../redux/actions/ParkAttendanceActions";

import { fetchDistricts, fetchMunicipalities } from "../redux/actions/locationActions";
import theme from "../components/shared/theme";
import {
  tdStyle,
  thStyle,
  paginationButtonStyle,
  dropdownStyle,
} from "../components/shared/themeStyles";

import { 
  FiFilter, 
  FiChevronLeft, 
  FiChevronRight,
  FiCalendar,
  FiRefreshCw,
  FiClock,
  FiMapPin,
  FiEye,
  FiDownload,
  FiUser
} from "react-icons/fi";

import Papa from "papaparse";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const ProjectAttendanceReport = () => {
  const dispatch = useDispatch();

  // Redux state
  const projectState = useSelector((state) => state.parkAttendance || {});
  
  const { 
    projectAttendance = [], 
    loading = false, 
    error = null, 
    pagination = {} 
  } = projectState;

  const locationState = useSelector((state) => state.location || {});
  const { districts = [], municipalities = [] } = locationState;

  // Set today's date as default
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filter states with today's date as default
  const [selectedProjectType, setSelectedProjectType] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  // Toast notification
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Project types for filter
  const projectTypes = [
    { value: "Park", label: "Park", color: "#4CAF50" },
    { value: "Shelter", label: "Shelter", color: "#2196F3" },
    { value: "Stretch", label: "Stretch", color: "#FF9800" }
  ];

  // Derived values for municipality filtering
  const selectedDistrictObj = useMemo(
    () => districts.find((d) => d.name === selectedDistrict) || null,
    [districts, selectedDistrict]
  );
  const selectedDistrictId = selectedDistrictObj?.id;

  const filteredMunicipalities = useMemo(
    () =>
      selectedDistrictId
        ? municipalities.filter((m) => m.district_id === selectedDistrictId)
        : municipalities,
    [municipalities, selectedDistrictId]
  );

  // Helper functions
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    return timeString.substring(0, 5);
  };

  // Fetch districts and municipalities on mount
  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
  }, [dispatch]);

  // Fetch data when filters change
  useEffect(() => {
    console.log("Fetching with filters:", {
      page: currentPage,
      limit: itemsPerPage,
      district: selectedDistrict,
      municipality: selectedMunicipality,
      from_date: fromDate,
      to_date: toDate,
      project_type: selectedProjectType,
    });
    
    dispatch(
      fetchProjectAttendance({
        page: currentPage,
        limit: itemsPerPage,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        from_date: fromDate,
        to_date: toDate,
        project_type: selectedProjectType,
      })
    );
  }, [
    dispatch,
    currentPage,
    selectedDistrict,
    selectedMunicipality,
    fromDate,
    toDate,
    selectedProjectType,
  ]);

  // Reset municipality when district changes
  useEffect(() => {
    setSelectedMunicipality("");
  }, [selectedDistrict]);

  // Reset filters
  const resetFilters = () => {
    setSelectedDistrict("");
    setSelectedMunicipality("");
    setFromDate(getTodayDate());
    setToDate(getTodayDate());
    setSelectedProjectType("");
    setCurrentPage(1);
    showToast("Filters reset to today's date", "info");
  };

  // CSV Export function
  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem("token") || "";
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        district: selectedDistrict || "",
        municipality: selectedMunicipality || "",
        from_date: fromDate,
        to_date: toDate,
        project_type: selectedProjectType || "",
      }).toString();

      const response = await fetch(
        `${BASE_URL}/v1/api/getProjectAttendance?${queryParams}`,
        {
          method: 'POST',
          headers: {
            authorization: token,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch data for export");
      }
      
      const json = await response.json();
      const rows = json?.data || [];

      // Prepare CSV headers
      const csvRows = [
        [
          "User ID",
          "Employee Name",
          "Designation",
          "District",
          "Municipality",
          "Project Code",
          "Project Type",
          "Location",
          "Attendance Date",
          "Status",
          "Check In Time",
          "Check Out Time",
          "Hours Worked",
          "Check In Photo",
          "Check Out Photo"
        ],
      ];

      // Add data rows
      rows.forEach((record) => {
        csvRows.push([
          record.user_id || "",
          record.name || "",
          record.designation || "",
          record.district || "",
          record.municipality || "",
          record.project_code || "",
          record.project_type || "",
          record.park_stretch_name || "",
          record.attendance_date || "",
          record.attendance_status || "",
          record.check_in || "",
          record.check_out || "",
          record.hours_worked || "",
          record.check_in_photo || "",
          record.check_out_photo || ""
        ]);
      });

      const csv = Papa.unparse(csvRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      const fileName = `Project_Attendance_${fromDate}_to_${toDate}.csv`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      
      showToast("Export completed successfully", "success");
    } catch (err) {
      console.error("Export error:", err);
      showToast("Export failed: " + err.message, "error");
    } finally {
      setExportLoading(false);
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    if (status === "Present") {
      return { backgroundColor: "#E8F5E9", color: "#2E7D32" };
    } else {
      return { backgroundColor: "#FFEBEE", color: "#C62828" };
    }
  };

  // Get project type badge color
  const getProjectTypeBadge = (type) => {
    switch(type) {
      case "Park": return { backgroundColor: "#4CAF50", color: "#fff" };
      case "Shelter": return { backgroundColor: "#2196F3", color: "#fff" };
      case "Stretch": return { backgroundColor: "#FF9800", color: "#fff" };
      default: return { backgroundColor: "#9E9E9E", color: "#fff" };
    }
  };

  // Get pagination values (handle different property names)
  const totalPages = pagination?.total_pages || pagination?.totalPages || 1;
  const currentPageFromAPI = pagination?.current_page || pagination?.currentPage || currentPage;
  const totalRecords = pagination?.total_records || pagination?.totalRecords || 0;

  return (
    <div style={styles.container}>
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          ...styles.toast,
          backgroundColor: toast.type === "success" ? "#4CAF50" : 
                         toast.type === "error" ? "#F44336" : "#2196F3"
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Project Attendance Report</h1>
          <p style={styles.subtitle}>Track attendance across all projects</p>
        </div>
        <div style={styles.headerActions}>
          <button
            onClick={handleExportCSV}
            disabled={exportLoading}
            style={{
              ...styles.iconButton,
              opacity: exportLoading ? 0.5 : 1,
              cursor: exportLoading ? "not-allowed" : "pointer",
              marginRight: "8px"
            }}
            title="Export to CSV"
          >
            <FiDownload size={20} color={theme.colors.primary} />
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={styles.iconButton}
            title="Toggle Filters"
          >
            <FiFilter size={20} color={showFilters ? theme.colors.primary : "#666"} />
          </button>
        </div>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div style={styles.filtersContainer}>
          <div style={styles.filtersGrid}>
            {/* Project Type Filter */}
            <div style={styles.projectTypeGroup}>
              <label style={styles.filterLabel}>Project Type</label>
              <div style={styles.projectTypeButtons}>
                {projectTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedProjectType(
                      selectedProjectType === type.value ? "" : type.value
                    )}
                    style={{
                      ...styles.projectTypeButton,
                      backgroundColor: selectedProjectType === type.value ? type.color : "#f5f5f5",
                      color: selectedProjectType === type.value ? "#fff" : "#666",
                      borderColor: type.color
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* District Filter */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>District</label>
              <select
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                style={styles.filterInput}
              >
                <option value="">All Districts</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.name}>{d.name}</option>
                ))}
              </select>
            </div>

            {/* Municipality Filter */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Municipality</label>
              <select
                value={selectedMunicipality}
                onChange={(e) => setSelectedMunicipality(e.target.value)}
                style={styles.filterInput}
                disabled={!selectedDistrict}
              >
                <option value="">All Municipalities</option>
                {filteredMunicipalities.map((m) => (
                  <option key={m.id} value={m.name}>{m.name}</option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Date Range</label>
              <div style={styles.dateRangeWrapper}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={styles.dateInput}
                  max={toDate || undefined}
                />
                <span style={styles.dateSeparator}>to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={styles.dateInput}
                  min={fromDate || undefined}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          </div>

          <div style={styles.filterActions}>
            <button onClick={resetFilters} style={styles.secondaryButton}>
              <FiRefreshCw size={16} />
              <span>Reset to Today</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content - Table View */}
      <div style={styles.contentContainer}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner} />
            <p>Loading attendance records...</p>
          </div>
        ) : error ? (
          <div style={styles.errorContainer}>
            <span style={{ color: "#F44336" }}>⚠️ {error}</span>
          </div>
        ) : projectAttendance.length === 0 ? (
          <div style={styles.emptyContainer}>
            <FiCalendar size={48} color="#ccc" />
            <p>No attendance records found for the selected criteria</p>
            <p style={{ fontSize: "14px", color: "#999", marginTop: "8px" }}>
              Try adjusting your filters or selecting a different date range
            </p>
            <button onClick={resetFilters} style={styles.secondaryButton}>
              Reset to Today
            </button>
          </div>
        ) : (
          <>
            {/* Table */}
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead style={styles.tableHead}>
                  <tr>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Designation</th>
                    <th style={thStyle}>Location</th>
                    <th style={thStyle}>Project</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Check In</th>
                    <th style={thStyle}>Check Out</th>
                    <th style={thStyle}>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {projectAttendance.map((record, index) => (
                    <tr key={record.id || index} style={styles.tableRow}>
                      <td style={tdStyle}>
                        <div style={styles.employeeCell}>
                          <FiUser size={14} color="#666" style={{ marginRight: "8px" }} />
                          <div>
                            <div style={styles.employeeName}>{record.name || "N/A"}</div>
                            <div style={styles.employeeId}>ID: {record.user_id || "N/A"}</div>
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>{record.designation || "-"}</td>
                      <td style={tdStyle}>
                        <div>
                          <div>{record.district || "-"}</div>
                          {record.municipality && (
                            <div style={styles.locationSub}>{record.municipality}</div>
                          )}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div>
                          <div style={styles.projectCode}>{record.project_code || "-"}</div>
                          {record.project_type && (
                            <span style={{
                              ...styles.projectTypeBadge,
                              ...getProjectTypeBadge(record.project_type)
                            }}>
                              {record.project_type}
                            </span>
                          )}
                          {record.park_stretch_name && (
                            <div style={styles.locationSub}>{record.park_stretch_name}</div>
                          )}
                        </div>
                      </td>
                      <td style={tdStyle}>{formatDate(record.attendance_date)}</td>
                      <td style={tdStyle}>
                        <span style={{
                          ...styles.statusBadge,
                          ...getStatusBadge(record.attendance_status)
                        }}>
                          {record.attendance_status || "-"}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {record.check_in ? (
                          <div>
                            <div>{formatTime(record.check_in)}</div>
                            {record.check_in_photo && (
                              <a 
                                href={record.check_in_photo} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={styles.photoLink}
                              >
                                Photo
                              </a>
                            )}
                          </div>
                        ) : "-"}
                      </td>
                      <td style={tdStyle}>
                        {record.check_out ? (
                          <div>
                            <div>{formatTime(record.check_out)}</div>
                            {record.check_out_photo && (
                              <a 
                                href={record.check_out_photo} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={styles.photoLink}
                              >
                                Photo
                              </a>
                            )}
                          </div>
                        ) : "-"}
                      </td>
                      <td style={tdStyle}>
                        {record.hours_worked ? (
                          <span style={styles.hoursBadge}>
                            {record.hours_worked}h
                          </span>
                        ) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination - FIXED VERSION */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    ...paginationButtonStyle,
                    ...(currentPage === 1 ? styles.disabledButton : {})
                  }}
                >
                  <FiChevronLeft size={20} />
                </button>
                
                <div style={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                  <span style={styles.totalRecords}>
                    ({totalRecords} records)
                  </span>
                </div>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    ...paginationButtonStyle,
                    ...(currentPage === totalPages ? styles.disabledButton : {})
                  }}
                >
                  <FiChevronRight size={20} />
                </button>
              </div>
            )}

            {/* Debug info - remove in production */}
            {process.env.NODE_ENV === 'development' && (
              <div style={styles.debugInfo}>
                <details>
                  <summary>Pagination Debug</summary>
                  <pre>{JSON.stringify(pagination, null, 2)}</pre>
                </details>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.background,
    minHeight: "100vh",
    position: "relative",
  },
  toast: {
    position: "fixed",
    top: "20px",
    right: "20px",
    padding: "12px 24px",
    borderRadius: "8px",
    color: "#fff",
    fontSize: "14px",
    fontWeight: "500",
    zIndex: 9999,
    animation: "slideIn 0.3s ease",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing.lg,
  },
  title: {
    fontSize: theme.font.size["3xl"],
    fontWeight: theme.font.weight.bold,
    color: theme.colors.grayDark,
    margin: 0,
  },
  subtitle: {
    fontSize: theme.font.size.sm,
    color: theme.colors.gray,
    marginTop: "4px",
  },
  headerActions: {
    display: "flex",
    gap: "8px",
  },
  iconButton: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    ':hover': {
      backgroundColor: theme.colors.grayLight,
    },
  },
  filtersContainer: {
    backgroundColor: "#fff",
    borderRadius: theme.radius.xl,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
    boxShadow: theme.shadow.soft,
  },
  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  projectTypeGroup: {
    gridColumn: "span 2",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  filterLabel: {
    fontSize: theme.font.size.sm,
    fontWeight: theme.font.weight.medium,
    color: theme.colors.grayDark,
  },
  filterInput: {
    ...dropdownStyle,
    height: "42px",
  },
  projectTypeButtons: {
    display: "flex",
    gap: "8px",
  },
  projectTypeButton: {
    flex: 1,
    padding: "10px 16px",
    borderRadius: "8px",
    border: "2px solid",
    fontSize: theme.font.size.sm,
    fontWeight: theme.font.weight.medium,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  dateRangeWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  dateInput: {
    ...dropdownStyle,
    flex: 1,
    height: "42px",
  },
  dateSeparator: {
    color: theme.colors.gray,
    fontSize: theme.font.size.sm,
  },
  filterActions: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: `1px solid ${theme.colors.border}`,
    paddingTop: theme.spacing.md,
  },
  secondaryButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    backgroundColor: "#fff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "8px",
    color: theme.colors.grayDark,
    fontSize: theme.font.size.sm,
    fontWeight: theme.font.weight.medium,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ':hover': {
      backgroundColor: theme.colors.grayLight,
    },
  },
  contentContainer: {
    backgroundColor: "#fff",
    borderRadius: theme.radius.xl,
    padding: theme.spacing.lg,
    boxShadow: theme.shadow.soft,
  },
  loadingContainer: {
    textAlign: "center",
    padding: "60px 20px",
    color: theme.colors.gray,
  },
  loadingSpinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: `3px solid ${theme.colors.primary}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "40px 20px",
    backgroundColor: "#FFEBEE",
    borderRadius: theme.radius.lg,
  },
  emptyContainer: {
    textAlign: "center",
    padding: "60px 20px",
    color: theme.colors.gray,
  },
  tableWrapper: {
    overflowX: "auto",
    marginBottom: theme.spacing.lg,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1200px",
    fontSize: theme.font.size.sm,
  },
  tableHead: {
    backgroundColor: theme.colors.grayDark,
    color: "#fff",
  },
  tableRow: {
    borderBottom: `1px solid ${theme.colors.border}`,
    ':hover': {
      backgroundColor: "#f5f5f5",
    },
  },
  employeeCell: {
    display: "flex",
    alignItems: "center",
  },
  employeeName: {
    fontWeight: theme.font.weight.semibold,
    color: theme.colors.grayDark,
  },
  employeeId: {
    fontSize: theme.font.size.xs,
    color: theme.colors.gray,
  },
  locationSub: {
    fontSize: theme.font.size.xs,
    color: theme.colors.gray,
    marginTop: "2px",
  },
  projectCode: {
    fontWeight: theme.font.weight.medium,
    color: theme.colors.grayDark,
  },
  projectTypeBadge: {
    fontSize: "10px",
    padding: "2px 6px",
    borderRadius: "10px",
    display: "inline-block",
    marginTop: "4px",
  },
  statusBadge: {
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: theme.font.size.xs,
    fontWeight: theme.font.weight.medium,
    display: "inline-block",
  },
  photoLink: {
    fontSize: theme.font.size.xs,
    color: theme.colors.primary,
    textDecoration: "underline",
    cursor: "pointer",
    marginTop: "2px",
    display: "inline-block",
  },
  hoursBadge: {
    backgroundColor: "#E3F2FD",
    color: "#1976D2",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: theme.font.size.xs,
    fontWeight: theme.font.weight.medium,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing.md,
    marginTop: theme.spacing.lg,
  },
  pageInfo: {
    fontSize: theme.font.size.sm,
    color: theme.colors.gray,
  },
  totalRecords: {
    marginLeft: "8px",
    color: theme.colors.gray,
    fontSize: theme.font.size.xs,
  },
  disabledButton: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  debugInfo: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.sm,
    backgroundColor: "#f0f0f0",
    borderRadius: theme.radius.sm,
    fontSize: theme.font.size.xs,
    overflow: "auto",
  },
};

// Add global keyframes
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default ProjectAttendanceReport;